import React, { useState, useEffect, useMemo } from "react";
import { X, Users, Shield, Layout, Clock, Send, AlertCircle, CheckCircle2, UserCheck, Search, Minimize2 } from "lucide-react";
import { CommanServices } from "../services/commanServices";
import { useDutyCheckJob } from "@/context/DutyCheckJobContext";
import DutyCheckSchedulePicker from "@/components/DutyCheckSchedulePicker";
import { useDutyCheckSchedule } from "@/hooks/useDutyCheckSchedule";

const SendDutyCheckModal = () => {
    const {
        modalOpen,
        closeModal,
        isRunning,
        isMinimized,
        setMinimized,
        startDutyCheck,
        departments,
        appUsers: users,
    } = useDutyCheckJob();

    const [targetType, setTargetType] = useState("all");
    const [selectedTargetIds, setSelectedTargetIds] = useState([]);
    const {
        schedule,
        patchSchedule,
        resetSchedule,
        summary: scheduleSummary,
        validate: validateSchedule,
        toPayload: scheduleToPayload,
    } = useDutyCheckSchedule();
    const [teams, setTeams] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [fetchingOptions, setFetchingOptions] = useState(false);
    const [error, setError] = useState(null);

    const isOpen = modalOpen && !isMinimized;

    useEffect(() => {
        if (!isOpen) return;
        if (isRunning) return;
        setTargetType("all");
        setSelectedTargetIds([]);
        resetSchedule();
        setUserSearch("");
        setError(null);
    }, [isOpen, isRunning, resetSchedule]);

    useEffect(() => {
        if (isOpen) {
            fetchOptions();
        }
    }, [isOpen, targetType]);

    const fetchOptions = async () => {
        if (targetType === "all" || targetType === "selected_users") return;
        setFetchingOptions(true);
        try {
            if (targetType === "team_crew") {
                const res = await CommanServices.getTeamsData();
                if (res?.status) {
                    setTeams(res.data || []);
                }
            }
        } catch (e) {
            console.error("Error fetching options:", e);
        } finally {
            setFetchingOptions(false);
        }
    };

    const userList = users;

    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q) return userList;
        return userList.filter(
            (u) =>
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                (u.user_account || "").toLowerCase().includes(q) ||
                (u.position || "").toLowerCase().includes(q)
        );
    }, [userList, userSearch]);

    const getUserLabel = (id) => {
        const u = userList.find((x) => x.id === id);
        return u?.name || u?.email || u?.user_account || id;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (targetType !== "all" && selectedTargetIds.length === 0) {
            setError("Please select at least one target");
            return;
        }

        const scheduleError = validateSchedule();
        if (scheduleError) {
            setError(scheduleError);
            return;
        }

        const schedulePayload = scheduleToPayload();
        const payload = {
            target_type: targetType,
            target_ids: selectedTargetIds,
            ...schedulePayload,
        };

        const result = await startDutyCheck(payload);
        if (result?.duplicate) {
            setError("A status check is already in progress.");
            return;
        }
        if (!result?.success && result?.message) {
            setError(result.message);
        }
    };

    if (!isOpen) return null;

    const toggleSelection = (id) => {
        setSelectedTargetIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden border border-gray-100 transform transition-all scale-100">
                {/* Header */}
                <div className="bg-[#0a1a3f] p-6 text-white flex justify-between items-center relative">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Send Status Check
                        </h2>
                        <p className="text-blue-100/70 text-xs mt-1">Target recipients and set timing for duty verification</p>
                    </div>
                    <div className="flex items-center gap-1 z-50">
                        {isRunning && (
                            <button
                                type="button"
                                onClick={() => setMinimized(true)}
                                className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                                aria-label="Minimize"
                                title="Minimize"
                            >
                                <Minimize2 className="w-5 h-5" />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={closeModal}
                            disabled={isRunning}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Close modal"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    {/* Decorative element */}
                    <div className="absolute right-[-20px] top-[-20px] w-24 h-24 bg-white/5 rounded-full blur-2xl"></div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {isRunning ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                            <div className="w-10 h-10 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                            <p className="text-gray-700 font-medium">Sending status check…</p>
                            <p className="text-sm text-gray-400">You can minimize and continue using the app.</p>
                            <button
                                type="button"
                                onClick={() => setMinimized(true)}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                            >
                                <Minimize2 className="w-4 h-4" />
                                Minimize
                            </button>
                        </div>
                    ) : (
                    <>
                    {/* Targeting Section */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Users className="w-4 h-4 text-indigo-600" />
                            Target Recipients
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                                { id: "all", label: "All Users", icon: Users },
                                { id: "team_crew", label: "Teams & Crews", icon: Shield },
                                { id: "department", label: "Depts", icon: Layout },
                                { id: "selected_users", label: "Selected Users", icon: UserCheck }
                            ].map((type) => (
                                <button
                                    key={type.id}
                                    type="button"
                                    onClick={() => {
                                        setTargetType(type.id);
                                        setSelectedTargetIds([]);
                                        setUserSearch("");
                                    }}
                                    className={`flex flex-col items-center justify-center p-3 rounded-2xl border-2 transition-all ${targetType === type.id
                                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                                        : "border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200"
                                        }`}
                                >
                                    <type.icon className="w-5 h-5 mb-1" />
                                    <span className="text-[11px] font-medium">{type.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Selection Area (Hidden for 'All') */}
                        {targetType !== "all" && (
                            <div className="mt-3 bg-gray-50 rounded-2xl p-4 border border-gray-100 max-h-60 overflow-y-auto">
                                {targetType === "selected_users" ? (
                                    <div className="space-y-3">
                                        {selectedTargetIds.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {selectedTargetIds.map((id) => (
                                                    <span
                                                        key={id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium border border-indigo-200"
                                                    >
                                                        {getUserLabel(id)}
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleSelection(id)}
                                                            className="hover:text-indigo-900"
                                                            aria-label={`Remove ${getUserLabel(id)}`}
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                            <input
                                                type="search"
                                                placeholder="Search users…"
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20"
                                            />
                                        </div>
                                        <div className="bg-white border border-gray-100 rounded-xl max-h-40 overflow-y-auto divide-y divide-gray-50">
                                            {userList.length === 0 ? (
                                                <p className="text-center text-gray-400 text-sm py-4">No users available</p>
                                            ) : filteredUsers.length === 0 ? (
                                                <p className="text-center text-gray-400 text-sm py-4">No users match your search</p>
                                            ) : (
                                                filteredUsers.map((u) => (
                                                    <label
                                                        key={u.id}
                                                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${selectedTargetIds.includes(u.id)
                                                            ? "bg-indigo-50 text-indigo-700"
                                                            : "hover:bg-gray-50 text-gray-600"
                                                            }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={selectedTargetIds.includes(u.id)}
                                                            onChange={() => toggleSelection(u.id)}
                                                        />
                                                        <div className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-colors ${selectedTargetIds.includes(u.id) ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"
                                                            }`}>
                                                            {selectedTargetIds.includes(u.id) && <CheckCircle2 className="w-3 h-3" />}
                                                        </div>
                                                        <span className="text-sm font-medium flex-1 truncate">
                                                            {u.name || u.email || u.user_account || "Unknown"}
                                                            {u.position ? (
                                                                <span className="text-gray-400 font-normal"> — {u.position}</span>
                                                            ) : null}
                                                        </span>
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : fetchingOptions ? (
                                    <div className="flex items-center justify-center py-4 gap-2 text-gray-400 text-sm">
                                        <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                        Loading options...
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {targetType === "department" ? (
                                            departments.map((opt) => (
                                                <label
                                                    key={opt.id}
                                                    className={`flex items-center gap-3 p-2 rounded-xl border cursor-pointer transition-colors ${selectedTargetIds.includes(opt.id)
                                                        ? "bg-white border-indigo-200 text-indigo-700 shadow-sm"
                                                        : "bg-transparent border-transparent hover:bg-white text-gray-600"
                                                        }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="hidden"
                                                        checked={selectedTargetIds.includes(opt.id)}
                                                        onChange={() => toggleSelection(opt.id)}
                                                    />
                                                    <div className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${selectedTargetIds.includes(opt.id) ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"
                                                        }`}>
                                                        {selectedTargetIds.includes(opt.id) && <CheckCircle2 className="w-3 h-3" />}
                                                    </div>
                                                    <span className="text-sm font-medium">
                                                        {opt.department_name}
                                                    </span>
                                                </label>
                                            ))
                                        ) : (
                                            teams.map((team) => (
                                                <div key={team.id} className="space-y-2">
                                                    <label
                                                        className={`flex items-center gap-3 p-2 rounded-xl border cursor-pointer transition-colors ${selectedTargetIds.includes(team.id)
                                                            ? "bg-white border-indigo-200 text-indigo-700 shadow-sm font-bold"
                                                            : "bg-white/50 border-gray-100 text-gray-800 font-semibold"
                                                            }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={selectedTargetIds.includes(team.id)}
                                                            onChange={() => toggleSelection(team.id)}
                                                        />
                                                        <div className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${selectedTargetIds.includes(team.id) ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"
                                                            }`}>
                                                            {selectedTargetIds.includes(team.id) && <CheckCircle2 className="w-3 h-3" />}
                                                        </div>
                                                        <span className="text-sm">Team: {team.team_name}</span>
                                                    </label>

                                                    {team.crews && team.crews.length > 0 && (
                                                        <div className="pl-6 space-y-1 border-l-2 border-dashed border-gray-200 ml-4">
                                                            {team.crews.map((crew) => (
                                                                <label
                                                                    key={crew.id}
                                                                    className={`flex items-center gap-3 p-2 rounded-xl border cursor-pointer transition-colors ${selectedTargetIds.includes(crew.id)
                                                                        ? "bg-white border-indigo-100 text-indigo-600 shadow-sm"
                                                                        : "bg-transparent border-transparent hover:bg-white text-gray-500"
                                                                        }`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        className="hidden"
                                                                        checked={selectedTargetIds.includes(crew.id)}
                                                                        onChange={() => toggleSelection(crew.id)}
                                                                    />
                                                                    <div className={`w-3.5 h-3.5 rounded-md border flex items-center justify-center transition-colors ${selectedTargetIds.includes(crew.id) ? "bg-indigo-500 border-indigo-500 text-white" : "border-gray-300"
                                                                        }`}>
                                                                        {selectedTargetIds.includes(crew.id) && <CheckCircle2 className="w-2.5 h-2.5" />}
                                                                    </div>
                                                                    <span className="text-[13px] font-medium">Crew: {crew.crew_name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}

                                        {targetType === "department" && departments.length === 0 && (
                                            <p className="text-center text-gray-400 text-sm py-4">No departments found</p>
                                        )}
                                        {targetType === "team_crew" && teams.length === 0 && (
                                            <p className="text-center text-gray-400 text-sm py-4">No teams or crews found</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DutyCheckSchedulePicker
                        schedule={schedule}
                        patchSchedule={patchSchedule}
                        summary={scheduleSummary}
                    />

                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-2xl text-xs animate-shake">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="flex-1 py-4 px-6 rounded-2xl text-sm font-bold text-gray-400 hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isRunning}
                            className={`flex-[2] py-4 px-6 rounded-2xl text-sm font-bold text-white shadow-xl shadow-indigo-200 transition-all flex items-center justify-center gap-2 ${isRunning ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]"
                                }`}
                        >
                            {schedule.deliveryMode === "now" ? "Initiate Blast" : "Schedule Check"}
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                    </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default SendDutyCheckModal;
